import InflectTablesModel, { IInflectIdName, IInflectTableCell } from 'models/InflectTablesModel';
import { get, put } from 'services/request';
import { IModelCourseOptions } from './BaseModel';
import { IRefIds } from '../constants/refs';
import BaseInflectsModel, { IInflectBase } from './BaseInflectsModel';

export enum IConjugationState {
  NO_CONJ,
  REGULAR,
  CHANGE,
  IRREGULAR,
  LOCK_NO_CONJ,
}

export type IInflect = IInflectBase & {
  word: string;
  conjWord: string;
  conjState: IConjugationState;
  conjLockState: IConjugationState;
  active?: boolean;
};

export type IInflectSource = IInflectBase & {
  word: string;
};

type IInflectWordClass = {
  wordClass: number;
};

export type IInflectId = Partial<IInflect> & {
  inflectId: number;
};

export type IInflectValue = {
  inflectId: number;
  value: string;
};

export type IInflectTableCellAugmented = IInflectTableCell & {
  values: IInflectBase[];
  rowHeader: IInflectIdName;
  columnHeader?: IInflectIdName;
};

export type IInflectTable = IInflectIdName & {
  inflects: IInflectTableCellAugmented[];
};

type IInflectConstructor = IModelCourseOptions & {
  courseId: number;
  targetDialectId?: number;
  ref: IRefIds;
  refId: number;
};

export default class InflectsModel extends BaseInflectsModel<IInflect | IInflectSource> {
  targetDialectId: number;
  ref: number;
  refId: number;
  inflectTablesModel: InflectTablesModel = undefined as any;
  activeInflectIds: number[] = [];

  // Refs that support active inflect templates
  private readonly refsWithActiveInflects = [IRefIds.panels, IRefIds.compounds];

  constructor(options: IInflectConstructor) {
    super(options);
    this.courseId = options.courseId;
    this.targetDialectId = options.targetDialectId ?? 0;
    this.ref = options.ref;
    this.refId = options.refId;
    this.loadItemEdit(this.refId);
  }

  async loadItemData(itemId: number) {
    const wordClass = await get<IInflectWordClass>(`refs/${this.ref}/ref-ids/${this.refId}/inflects-wordClass`);
    this.inflectTablesModel = new InflectTablesModel({
      courseId: this.courseId,
      wordClass: wordClass.wordClass,
      targetDialectId: this.targetDialectId,
    });

    const promises: Promise<any>[] = [
      this.inflectTablesModel.getInflectTables(),
      this.inflectTablesModel.getInflectTableHeaders(),
      this.inflectTablesModel.getInflectTableCells(),
    ];

    // Load active inflect IDs for supported refs
    if (this.refsWithActiveInflects.includes(this.ref)) {
      promises.push(
        get<number[]>(`refs/${this.ref}/ref-ids/${this.refId}/active-inflects`).then((ids) => {
          this.activeInflectIds = ids;
        }),
      );
    }

    await Promise.all(promises);

    const items = await get<IInflect[]>(
      `refs/${this.ref}/ref-ids/${this.refId}/dialects/${this.targetDialectId}/inflects`,
    );

    return this.fillInflects(items);
  }

  async save() {
    this.loadingItem = true;
    this.render();
    await put(`refs/${this.ref}/ref-ids/${this.refId}/dialects/${this.targetDialectId}/inflects`, {
      inflects: JSON.stringify(this.itemUpdated),
    });
    await this.loadItemEdit(this.refId);
  }

  async autoInflect() {
    this.loadingItem = true;
    this.render();
    await put(`refs/${this.ref}/ref-ids/${this.refId}/dialects/${this.targetDialectId}/auto-inflect`, {
      inflects: '',
    });
    await this.loadItemEdit(this.refId);
  }

  protected createEmptyItem(inflectId: number, index = 0): IInflect | IInflectSource {
      const item: IInflect = {
        index,
        inflectId,
        conjLockState: IConjugationState.NO_CONJ,
        conjState: IConjugationState.NO_CONJ,
        conjWord: '',
        word: '',
      };

      if (this.refsWithActiveInflects.includes(this.ref)) {
        item.active = this.activeInflectIds.includes(inflectId);
      }

      return item;
  }

  isInflectEmpty(item: IInflect): boolean {
    return item.word === '';
  }

  protected fillInflects(items: IInflect[] | IInflectSource[]) {
    const filled = super.fillInflects(items);

    // Add active property for refs with active inflects support
    if (this.refsWithActiveInflects.includes(this.ref)) {
      return filled.map((item) => ({
        ...item,
        active: this.activeInflectIds.includes(item.inflectId),
      }));
    }

    return filled;
  }
}
